import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthRoutesConfig } from "features/auth/navigation/config";
import { useModal } from "components/common/Modal";
import TermsOfUse from "../../features/terms-and-conditions/components/TermsOfUse";
import { useTermsContext } from "features/terms-and-conditions/components/TermsContext";
import { useMemberInfo } from "../MemberInfoProvider/MemberInfoProvider";
import { MemberDetails } from "orval/generated/models";
import useCheckAuthState from "./useCheckAuthState";
import { Permission, permissionRouteMap } from "./permission";
import { SupportingPagesRoutesConfig } from "features/supporting-pages";

interface ProtectedRoutesProps {
  children: React.ReactNode;
}

const checkPermissions = (
  memberInfo: MemberDetails | null,
  routePermission: Permission | undefined,
  hasPermission: (permission: string) => boolean,
): boolean => {
  if (memberInfo && memberInfo?.permissions.functional_access?.length < 1)
    return false;
  if (!routePermission) return true;
  if (!memberInfo) {
    return false;
  }
  return hasPermission(routePermission);
};

const ProtectedRoutes: React.FC<ProtectedRoutesProps> = ({
  children,
}) => {
  const location = useLocation();
  const url = location.pathname + location.search;
  const authFailureRoute = AuthRoutesConfig.idamLogin;
  const [isSignedIn] = useCheckAuthState(url ?? "", authFailureRoute);
  const { openModal, closeModal } = useModal();
  const { isTermsAccepted, setIsTermsAccepted } = useTermsContext();
  const { memberInfo, hasPermission } = useMemberInfo();
  const routePermission = permissionRouteMap[location.pathname];
  const navigate = useNavigate();

  useEffect(() => {
    if (
      isSignedIn &&
      isTermsAccepted === true &&
      memberInfo &&
      !checkPermissions(memberInfo, routePermission, hasPermission)
    ) {
      navigate(SupportingPagesRoutesConfig.accessDenied);
    }
  }, [
    isSignedIn,
    isTermsAccepted,
    memberInfo,
    routePermission,
    hasPermission,
    navigate,
  ]);

  if (isSignedIn) {
    if (isTermsAccepted === false) {
      openModal(
        <TermsOfUse
          onClose={closeModal}
          setIsTcsAccepted={setIsTermsAccepted}
        />,
        true,
      );
      return <>{children}</>;
    } else if (isTermsAccepted === true && memberInfo) {
      if (!checkPermissions(memberInfo, routePermission, hasPermission)) {
        return <div />;
      }

      return <>{children}</>;
    }
  }
  return <div />;
};

export default ProtectedRoutes;
